import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import { planService } from '../../services/planService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Eye, Edit, Search } from 'lucide-react';

const UserList = ({ onUserSelect }) => {
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const { t, language } = useLanguage();

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm]);

    useEffect(() => {
        planService.getAllPlans().then(response => {
            if (response.success) setPlans(response.data.plans || []);
        }).catch(error => console.error(t('loadUsersError'), error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await userService.getAllUsers(currentPage, 20, searchTerm);

            if (response.success) {
                setUsers(response.data.users || []);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error(t('loadUsersError'), error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    // Paketler artık admin panelden dinamik oluşturuluyor (bkz. Paketler sekmesi) - sabit
    // free/silver/gold switch'i yeni bir paket (ör. "Platinum") eklendiğinde onu yanlışlıkla
    // "free" gibi göstererek yanıltıcı olurdu. Slug'ı doğrudan class adı olarak kullanıyoruz;
    // bilinen 3 paket için scss zaten .free/.silver/.gold tanımlıyor, yeni paketler nötr kalır.
    const getPlanBadgeClass = (plan) => plan || 'free';

    const getStatusBadgeClass = (isActive) => {
        return isActive ? 'active' : 'inactive';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const handlePlanUpdate = async (userId, newPlan) => {
        try {
            await userService.updateUserPlan(userId, newPlan);
            loadUsers();
        } catch (error) {
            console.error(t('planUpdateError'), error);
        }
    };

    const handleStatusToggle = async (userId, currentStatus) => {
        try {
            await userService.toggleUserStatus(userId, !currentStatus);
            loadUsers();
        } catch (error) {
            console.error(t('statusUpdateError'), error);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px'
            }}>
                <div>{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="user-management">
            <div className="page-header">
                <h1>{t('userManagement')}</h1>
                <p>{t('userManagementDescription')}</p>
            </div>

            <div className="users-content">
                <div className="users-stats">
                    <div className="stat-card">
                        <div className="stat-value">{users.length}</div>
                        <div className="stat-label">{t('totalUsers')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {users.filter(u => u.plan !== 'free').length}
                        </div>
                        <div className="stat-label">{t('premiumUsers')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {users.filter(u => u.is_active).length}
                        </div>
                        <div className="stat-label">{t('activeUsers')}</div>
                    </div>
                </div>

                <div className="users-table-container">
                    <div className="table-header">
                        <h2>{t('allUsers')}</h2>
                        <div className="table-actions">
                            <div className="search-box">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder={t('searchUsers')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                    </div>

                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>{t('id')}</th>
                                <th>{t('email')}</th>
                                <th>{t('name')}</th>
                                <th>{t('plan')}</th>
                                <th>{t('status')}</th>
                                <th>{t('registrationDate')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>{user.email}</td>
                                    <td>{user.name}</td>
                                    <td>
                                        <select
                                            value={user.plan}
                                            onChange={(e) => handlePlanUpdate(user.id, e.target.value)}
                                            className={`plan-select ${getPlanBadgeClass(user.plan)}`}
                                        >
                                            {plans.map(plan => (
                                                <option key={plan.slug} value={plan.slug}>
                                                    {language === 'tr' ? plan.nameTr : plan.nameEn}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <button
                                            className={`status-toggle ${getStatusBadgeClass(user.is_active)}`}
                                            onClick={() => handleStatusToggle(user.id, user.is_active)}
                                        >
                                            {user.is_active ? t('active') : t('inactive')}
                                        </button>
                                    </td>
                                    <td>{formatDate(user.created_at)}</td>
                                    <td>
                                        <div className="user-actions">
                                            <button
                                                className="view-btn"
                                                onClick={() => onUserSelect(user)}
                                                title={t('viewDetails')}
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button
                                                className="edit-btn"
                                                title={t('editUser')}
                                            >
                                                <Edit size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {users.length === 0 && !loading && (
                        <div className="no-data">
                            <p>{t('noUsersFound')}</p>
                        </div>
                    )}

                    <div className="pagination">
                        <div className="pagination-info">
                            {t('page')} {currentPage} / {totalPages}
                        </div>
                        <div className="pagination-controls">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                            >
                                {t('previous')}
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                            >
                                {t('next')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserList;